/**
 * The card's picture (client, 2026-09-15: *"clients want thumbnails on each data card"*).
 *
 * One `<img>` per card, lazy-loaded, pointing at `GET /datasets/{id}/thumbnail.png`. The
 * server decides what that is — a styled TiTiler preview for a raster, a drawn PNG for an
 * API vector layer — and 404s for the rest (external WMS, table-only records). nginx turns
 * a missing route into the SPA's own index.html with a 200, but the API route exists and
 * answers a real 404, and either way the `<img>` fires `error`, which is the one signal that
 * cannot be fooled (NOTES: the same rule the sign-in reel uses). On error the box shows the
 * fallback tile instead, so a card never carries a broken-image icon.
 *
 * **The fallback is a tile, not a glyph** (client, 2026-09-16: *"make the Data catalogue
 * highly professional, it looks cluttered"*). A third of the catalogue is tabular, and a
 * grid of identical grey database icons read as a rendering failure rather than as a row
 * type. Instead the box is tinted in the row's own domain colour and carries the source
 * monogram ("MEL", "ISR"), so a screenful of table rows is still legible as *whose* tables
 * they are. Rows with no domain fall back to the neutral ground the box always had, and a
 * row with neither a usable source nor a domain keeps that ground with nothing printed on
 * it — still a tile, never a blank card.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { datasetThumbnailUrl, type DatasetDomainFacet, type DatasetRow } from '../../lib/api';
import { Icon } from '../../lib/icons';
import { dataTypeIcon, tileMonogram } from './catalogue';

export interface DatasetThumbProps {
  row: DatasetRow;
  /** The row's domain facet, for the fallback tile's tint. Absent → the neutral ground. */
  domain?: DatasetDomainFacet;
}

export function DatasetThumb({ row, domain }: DatasetThumbProps) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  // No layer, no picture to ask for: skip the request rather than collect a 404 per card.
  const askable = row.layer_ids.length > 0 && !failed;
  // A one- or two-letter source is not a monogram: the tile falls back to the domain's
  // own short code, and to a plain tinted ground when there is neither.
  const monogram = tileMonogram(row.source_org, domain?.name);
  // `33` is a 20 % alpha, `14` an 8 % one. At 12 % flat the domains were not tellable apart
  // across a grid — the whole point of tinting them — and 20 % still reads as a wash the
  // monogram sits on rather than a block of colour it has to fight. Since 2026-09-16 the
  // two are the ends of a diagonal ramp rather than one flat fill: a tile then catches the
  // light the way the card's own shadow says it should, in the domain's own colour and with
  // no second hue introduced.
  const tint = domain
    ? {
        background: `linear-gradient(135deg, ${domain.color}33, ${domain.color}14)`,
        color: domain.color,
      }
    : undefined;
  return (
    <div
      className={`ds-thumb${askable ? '' : ' ds-thumb-empty'}`}
      data-testid={`ds-thumb-${row.id}`}
      style={askable ? undefined : tint}
    >
      {askable ? (
        <img
          src={datasetThumbnailUrl(row.id)}
          alt={t('catalogue.thumbAlt', { title: row.title })}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="ds-thumb-mono" aria-hidden="true">
          {monogram}
        </span>
      )}
    </div>
  );
}

/**
 * The data-type glyph a card wears on the bottom-left of its picture (client, 2026-09-16:
 * *"still not happy with our modernity and creativity on the catalogue"*).
 *
 * The card already said "COG" or "CSV" in its meta line, in 10.5 px grey, at the bottom —
 * which is a *format*, and a reader scanning a screenful of cards is asking the coarser
 * question first: is this a picture, a set of shapes, or a table? One glyph answers it
 * before any word is read, and the picture's own bottom-left corner is where it can sit
 * without displacing anything.
 *
 * It rides a near-white pill rather than a tinted one: the ground under it is a
 * server-rendered thumbnail that can be any colour at all, so the pill provides its own
 * contrast and the *ink* carries the domain's colour. The same rule as the "No access" lock
 * on the opposite corner — and the same shadow, so the two read as one family.
 *
 * `aria-label` and not a bare `title`: the glyph is the only place a card says its shape,
 * so it has to be a labelled image to a screen reader, not decoration.
 */
export function DatasetTypeBadge({ row, domain }: DatasetThumbProps) {
  const { t } = useTranslation();
  const label = t(`catalogue.type.${row.data_type}`);
  return (
    <span
      className="ds-badge ds-badge-type"
      data-testid={`ds-type-${row.id}`}
      role="img"
      aria-label={label}
      title={label}
      style={domain ? { color: domain.color } : undefined}
    >
      <Icon name={dataTypeIcon(row.data_type)} size={12} />
    </span>
  );
}
